import i18n from 'i18next'
import { createContext, useState, useMemo, useEffect } from 'react'
import { cartDataInit, colegialDataInit, formDataInit } from '../utils/data'
import { cartDataTypes, colegialInitType } from '../types/Views'
import { setItem, getItem } from '../utils/LocalStorageManager'

interface UIContextType {
  reserved: boolean
  setReserved?: (value: boolean) => void
  language: string
  setLanguage?: (value: string) => void
  handleLangChange?: (lang: string) => void
  loading: boolean
  setLoading?: (value: boolean) => void
  successMsg: string
  setSuccessMsg?: (value: string) => void
  resetSuccessMsg?: () => void
  resetErrorMsg?: () => void
  errorMsg: string
  setErrorMsg?: (value: string) => void
  colegial: colegialInitType
  setColegial?: (value: colegialInitType) => void
  cartData: cartDataTypes
  setCartData?: (value: cartDataTypes) => void
  disabledFullday: boolean
  setDisabledFullday?: (value: boolean) => void
  payOrder: any
  setPayOrder?: (value: any) => void
  haveAssociated: boolean
  setHaveAssociated?: (value: boolean) => void
  activeTab: number
  setActiveTab?: (value: number) => void
  handleRestart?: () => void
  darkMode: boolean
  setDarkMode?: (value: boolean) => void
}

const initialState: UIContextType = {
  reserved: false,
  language: 'es',
  loading: false,
  successMsg: '',
  errorMsg: '',
  colegial: colegialDataInit,
  cartData: cartDataInit,
  disabledFullday: false,
  haveAssociated: false,
  payOrder: 0,
  activeTab: 0,
  darkMode: true
}

export const UIContext = createContext<UIContextType>(initialState)

export const UIProvider = ({ children }: { children: React.ReactNode }) => {
  const [reserved, setReserved] = useState(initialState.reserved)
  const [language, setLanguage] = useState(initialState.language)
  const [loading, setLoading] = useState(false)
  const [successMsg, setSuccessMsg] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [colegial, setColegial] = useState<colegialInitType>(colegialDataInit)
  const [cartData, setCartData] = useState<cartDataTypes>(cartDataInit)
  const [disabledFullday, setDisabledFullday] = useState<boolean>(false)
  const [payOrder, setPayOrder] = useState<any>()
  const [haveAssociated, setHaveAssociated] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [darkMode, setDarkMode] = useState(initialState.darkMode)

  // Cargar el modo oscuro desde localStorage al iniciar
  useEffect(() => {
    const loadDarkMode = async () => {
      const savedDarkMode = await getItem('darkMode')
      if (savedDarkMode !== null) {
        setDarkMode(savedDarkMode === 'true')
      }
    }
    loadDarkMode()
  }, [])

  // Guardar el modo oscuro en localStorage cuando cambie
  useEffect(() => {
    setItem('darkMode', darkMode.toString())
  }, [darkMode])

  const resetErrorMsg = () => {
    setErrorMsg('')
  }

  const resetSuccessMsg = () => {
    setSuccessMsg('')
  }

  const handleLangChange = async (lang: string) => {
    setLanguage(lang)
    await setItem('lang', lang)
    const localLang: any = await getItem('lang')
    i18n.changeLanguage(localLang)
  }

  const handleRestart = () => {
    setCartData(cartDataInit)
    setActiveTab(0)
    setColegial(colegialDataInit)
    setReserved(false)
  }

  const value = useMemo(
    () => ({
      reserved,
      setReserved,
      language,
      setLanguage,
      handleLangChange,
      loading,
      setLoading,
      successMsg,
      setSuccessMsg,
      resetSuccessMsg,
      resetErrorMsg,
      errorMsg,
      setErrorMsg,
      colegial,
      setColegial,
      cartData,
      setCartData,
      disabledFullday,
      setDisabledFullday,
      payOrder,
      setPayOrder,
      haveAssociated,
      setHaveAssociated,
      activeTab,
      setActiveTab,
      handleRestart,
      darkMode,
      setDarkMode
    }),
    [
      reserved,
      language,
      loading,
      successMsg,
      errorMsg,
      colegial,
      cartData,
      disabledFullday,
      payOrder,
      haveAssociated,
      activeTab,
      darkMode
    ]
  )

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>
}
